import { COUNTRIES } from '../../config/countries';
import type { Traveller } from '../../api/types/traveller';

/**
 * Maps a saved `Traveller` onto the `AirPassengerForm` passenger-row fields (legacy
 * `allocated.js` `.paxlookup-identity` select handler). The saved record's formats are
 * loose (dates as "dd.MM.yyyy", gender / nationality as free text), so each value is
 * normalised to what the React form's inputs expect: `<input type="date">` → "yyyy-MM-dd",
 * the gender select → "Male"/"Female", the nationality select → an ISO country code.
 */

/** "dd.MM.yyyy" / "dd/MM/yyyy" / "yyyy-MM-dd" (with optional trailing time) → "yyyy-MM-dd",
 *  or "" when unparseable. */
export function toDateInputValue(value?: string | null): string {
  if (!value) return '';
  const v = value.trim();
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = v.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return '';
}

/** Free-text gender ("Male"/"Erkek"/"M"/"K"/…) → the form select's "Male"/"Female", or "". */
export function normalizeGender(value?: string | null): '' | 'Male' | 'Female' {
  if (!value) return '';
  const v = value.trim().toLowerCase();
  if (v.startsWith('m') || v.startsWith('e') || v === 'bay') return 'Male';
  if (v.startsWith('f') || v.startsWith('k') || v.startsWith('w') || v === 'bayan') return 'Female';
  return '';
}

/** "TR" / "Türkiye" / "Turkey" → an ISO code the nationality `<select>` has an option
 *  for; falls back to the raw value so a valid code from our own data passes through. */
export function resolveNationality(value?: string | null): string {
  if (!value) return '';
  const v = value.trim();
  const upper = v.toUpperCase();
  if (COUNTRIES.some((c) => c.code === upper)) return upper;
  const byName = COUNTRIES.find((c) => c.name.toLowerCase() === v.toLowerCase());
  if (byName) return byName.code;
  if (/^t[uü]rk/i.test(v)) return 'TR';
  return v;
}

export interface PassengerPrefill {
  firstName: string;
  lastName: string;
  gender: '' | 'Male' | 'Female';
  birthDate: string;
  nationality: string;
  citizenNo: string;
  passportNo: string;
  passportExpireDate: string;
  frequentFlayerNo: string;
}

export function travellerToPassengerPrefill(t: Traveller): PassengerPrefill {
  return {
    firstName: t.firstName?.trim() ?? '',
    lastName: t.lastName?.trim() ?? '',
    gender: normalizeGender(t.gender),
    birthDate: toDateInputValue(t.birthDate),
    nationality: resolveNationality(t.nationality) || 'TR',
    citizenNo: t.identity?.trim() ?? '',
    passportNo: t.passport?.trim() ?? '',
    passportExpireDate: toDateInputValue(t.passportExpireDate),
    frequentFlayerNo: t.frequentFlyerNumber?.trim() ?? '',
  };
}

/** Traveller phone → the contact form's local-number field ("+90 555…" / "0090555…" →
 *  "555…", leaving the region select on its default). */
export function toLocalPhone(raw?: string | null): string {
  if (!raw) return '';
  let d = raw.replace(/\D/g, '');
  if (d.startsWith('0090')) d = d.slice(4);
  else if (d.startsWith('90') && d.length >= 12) d = d.slice(2);
  return d;
}
